import { useState, useEffect } from 'react';
import { LuSearch, LuRefreshCw } from 'react-icons/lu';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import LoadDrawer from '../../components/LoadDrawer/LoadDrawer';
import TableCard from '../../components/TableCard/TableCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import { fetchLoads } from '../../lib/directus';
import styles from './BidSelection.module.css';

const STATUS_OPTIONS = ['All status', 'Pending', 'Open', 'Assigned', 'Completed'];

function mapLoad(load) {
  return {
    _id: load.id,
    id: load.load_id || load.id.slice(0, 8).toUpperCase(),
    pickup: load.pickup_location || '-',
    delivery: load.delivery_location || '-',
    cargo: load.order?.batch?.commodity || '-',
    distance: '-',
    pickupDate: '-',
    suggestedRate: load.suggested_rate
      ? `R${parseFloat(load.suggested_rate).toLocaleString()}`
      : '-',
    estimatedDuration: '-',
    status: load.bidding_status || 'Pending',
    biddingClosed: load.date_updated
      ? new Date(load.date_updated).toLocaleDateString('en-ZA', {
          day: 'numeric', month: 'short', year: 'numeric',
        })
      : '-',
    seller: load.order?.batch?.seller?.company_name || '-',
    buyer:  load.order?.buyer?.company_name || '-',
    transporter: load.awarded_transporter?.company_name || null,
    bidsCount: load.bids?.length || 0,
    bids: (load.bids || []).map((bid) => ({
      bidId: bid.id,
      transporter: bid.transporter?.company_name || '-',
      transporterId: bid.transporter?.id || null,
      transporterUserId: bid.transporter?.contact || null,
      trips: '-',
      price: bid.bid_amount
        ? `R${parseFloat(bid.bid_amount).toLocaleString()}`
        : '-',
    })),
  };
}

export default function BidSelection() {
  const [loads, setLoads]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('All status');
  const [activeLoad, setActiveLoad] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchLoads();
      console.log('fetchLoads:', data);
      setLoads(data.map(mapLoad));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = loads.filter((row) => {
    const matchSearch =
      row.id.toLowerCase().includes(search.toLowerCase()) ||
      row.pickup.toLowerCase().includes(search.toLowerCase()) ||
      row.delivery.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All status' || row.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const toolbar = (
    <>
      <div className={styles.toolbarLeft}>
        <div className={styles.searchWrapper}>
          <LuSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search loads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <CustomSelect
          value={statusFilter}
          onChange={setStatus}
          options={STATUS_OPTIONS}
        />
      </div>

      <button
        className={styles.refreshBtn}
        onClick={() => { setSearch(''); setStatus('All status'); loadData(); }}
      >
        <LuRefreshCw size={13} />
        Refresh
      </button>
    </>
  );

  const pagination = (
    <>
      <span className={styles.pageInfo}>Total {filtered.length} items</span>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button className={`${styles.pageBtn} ${styles.pageBtnActive}`}>1</button>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </>
  );

  return (
    <AdminLayout title="Bid selection">
      <LoadDrawer
        load={activeLoad}
        onClose={() => setActiveLoad(null)}
        onAwarded={() => { setActiveLoad(null); loadData(); }}
      />
      <TableCard toolbar={toolbar} pagination={pagination}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Load ID</th>
              <th>Route</th>
              <th>Cargo</th>
              <th>Bids</th>
              <th>Bidding closed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--secondary-text)' }}>
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--secondary-text)' }}>
                  No loads found.
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row._id} onClick={() => setActiveLoad(row)} style={{ cursor: 'pointer' }}>
                  <td className={styles.loadId}>{row.id}</td>
                  <td>
                    <div className={styles.route}>
                      <span>{row.pickup}</span>
                      <span className={styles.routeArrow}>→</span>
                      <span>{row.delivery}</span>
                    </div>
                  </td>
                  <td>{row.cargo}</td>
                  <td>{row.bidsCount}</td>
                  <td>{row.biddingClosed}</td>
                  <td><StatusBadge status={row.status} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>
    </AdminLayout>
  );
}
